import { parseDateRangeFromQuery, parseFiltersFromQuery, createFilterQuery } from './filters/filterQueryUtils'
import store from '@/store'

// A map from platform string to the key in the report config that contains the platform specific data
const platformConfigKeyMap = {
  'tiktok': 'tiktok_config',
}

// Multiplatform handler schemas for separating platform data in GET and POST requests
// Keys are object fields that will have different values for each platform
// Values are the defaults

// GET request fields that will have different values for each platform
const PLATFORM_SPECIFIC_GET_FIELDS = {
  'columns_hash': null,
  'table_columns': [
    { key: 'spend', is_pinned: false }, 
    { key: 'impressions', is_pinned: false }, 
    { key: 'roas', is_pinned: false }
  ],
  'selected_columns': {
    'bar': ['spend', 'impressions', 'roas'],
    'grid': ['spend', 'impressions', 'roas'],
    'line': ['spend', 'roas'],
  },
  'sorted_column': { by: 'spend', order: 'desc' },
  'preset_id': null,
  'preset': null,
  'filters': [
    [{ key: 'event_date', operation: 'greater_than', value: 'last_7_days' }],
    [{ key: 'event_date', operation: 'less_than', value: 'today' }]
  ],
  'custom_groups_filters': []
}

// POST request fields that will have different values for each platform
const PLATFORM_SPECIFIC_POST_FIELDS = {
  'table_columns': [
    { key: 'spend', is_pinned: false }, 
    { key: 'impressions', is_pinned: false }, 
    { key: 'roas', is_pinned: false }
  ],
  'selected_columns': {
    'bar': ['spend', 'impressions', 'roas'],
    'grid': ['spend', 'impressions', 'roas'],
    'line': ['spend', 'roas'],
  },
  'sorted_column': { by: 'spend', order: 'desc' },
  'preset_id': null,
  'filters': [
    [{ key: 'event_date', operation: 'greater_than', value: 'last_7_days' }],
    [{ key: 'event_date', operation: 'less_than', value: 'today' }]
  ],
  'custom_groups_filters': []
}

const getPrependedPlatformConfigFields = () => {
  const prependedFieldKeys = Object.values(platformConfigKeyMap).flatMap(platformConfigKey => {
    return Object.keys(PLATFORM_SPECIFIC_POST_FIELDS).map(fieldKey => `${platformConfigKey}.${fieldKey}`)
  })
  return prependedFieldKeys
}

const defaultMissingPlatformConfigFields = (reportConfig) => {
  const newConfig = { ...reportConfig }
  Object.values(platformConfigKeyMap).forEach(platformConfigKey => {
    if (!newConfig?.[platformConfigKey]) {
      newConfig[platformConfigKey] = {}
    }
    for (const fieldKey in PLATFORM_SPECIFIC_GET_FIELDS) {
      if (!newConfig[platformConfigKey]?.[fieldKey]) {
        newConfig[platformConfigKey][fieldKey] = PLATFORM_SPECIFIC_GET_FIELDS[fieldKey]
      }
    }
  })
  return newConfig
}

// Builds a report state object from a report config returned by the API
const getReportStateFromConfig = (reportConfig, overridePlatform = null) => {
  const platform = overridePlatform ?? store.getters['MetricsModule/getMetricsPlatform']
  const { tiktok_config = null, ...metaReport } = reportConfig
  if (platform === 'meta') {
    return metaReport
  } else {
    const tiktokData = {}
    Object.entries(PLATFORM_SPECIFIC_GET_FIELDS).forEach(([fieldKey, defaultVal]) => {
      tiktokData[fieldKey] = tiktok_config?.[fieldKey] ?? defaultVal
    })
    return {
      ...metaReport,
      ...tiktokData // Override the platform specific fields with the tiktok config
    }
  }
}

// Take a report config, a change object, and platform. Returns an updated report config
const applyChangeToReportConfig = (reportConfig, change, overridePlatform = null) => {
  const platform = overridePlatform ?? store.getters['MetricsModule/getMetricsPlatform']
  const { type, value } = change

  let newConfig = {}
  const isPlatformField = platform !== 'meta' && Object.keys(PLATFORM_SPECIFIC_POST_FIELDS).includes(type)
  if (isPlatformField) {
    const platformConfig = reportConfig[platformConfigKeyMap[platform]] ?? {}
    newConfig = {
      ...reportConfig,
      [platformConfigKeyMap[platform]]: {
        ...platformConfig,
        [type]: value
      }
    }
  } else {
    newConfig = {
      ...reportConfig,
      [type]: value
    }
  }

  // For date range changes, we want to apply the date range to all platforms - use a special process
  const dateRange = type === 'filters' ? parseDateRangeFromQuery(value) : null
  if (dateRange?.start && dateRange?.end) {
    newConfig.filters = _replaceDateRangeFilters(newConfig.filters, dateRange)
    Object.values(platformConfigKeyMap).forEach(platformConfigKey => {
      newConfig[platformConfigKey].filters = _replaceDateRangeFilters(newConfig[platformConfigKey].filters, dateRange)
    })
  }

  return newConfig
}

const mergeUpdateToReportConfig = (reportConfig, toUpdate, overridePlatform = null) => {
  const platform = overridePlatform ?? store.getters['MetricsModule/getMetricsPlatform']
  return Object.entries(toUpdate).reduce((acc, [key, value]) => (
    applyChangeToReportConfig(acc, { type: key, value }, platform)
  ), { ...reportConfig })
}

// Transforms a field value for all platform configs
// Does nothing if the field is not found in the platform config
const transformFieldValueAllPlatforms = (reportConfig, fieldKey, transformFunc) => {
  const newConfig = {
    ...reportConfig,
    [fieldKey]: transformFunc(reportConfig[fieldKey])
  }
  Object.values(platformConfigKeyMap).forEach(platformConfigKey => {
    const platformFieldValue = newConfig?.[platformConfigKey]?.[fieldKey]
    if (platformFieldValue) {
      newConfig[platformConfigKey][fieldKey] = transformFunc(platformFieldValue)
    }
  })
  return newConfig
}

// Replace the date range filters in a filter query with a new date range
const _replaceDateRangeFilters = (filters, newDateRange) => {
  const existingNonDateFilters = parseFiltersFromQuery(filters)
  return createFilterQuery(existingNonDateFilters, newDateRange)
}

export {
  platformConfigKeyMap,
  getPrependedPlatformConfigFields,
  defaultMissingPlatformConfigFields,
  getReportStateFromConfig,
  applyChangeToReportConfig,
  mergeUpdateToReportConfig,
  transformFieldValueAllPlatforms
}